type SplitLikeItem = {
  _id?: string;
  temp_item_id?: string;
  ordered_line_id?: string;
  menu_item_id?: string;
  order_id?: string;
  source_order_id?: string;
  item_name?: string;
  name?: string;
  quantity?: number;
  price?: number;
  consumption_tax_rate?: number;
};

export const getOrderedLineId = (item: {
  ordered_line_id?: unknown;
  temp_item_id?: unknown;
}): string => String(item.ordered_line_id || item.temp_item_id || "");

export const getSplitItemKey = (item: Partial<SplitLikeItem>): string =>
  (getOrderedLineId(item)
    ? [String(item.order_id || item.source_order_id || ""), getOrderedLineId(item)].join("|")
    : "") ||
  [
    String(item.order_id || ""),
    String(item.temp_item_id || item._id || item.menu_item_id || item.item_name || item.name || ""),
    item.price ?? "",
    item.consumption_tax_rate ?? "",
  ].join("|");

/**
 * Financial identity of an order line, mirroring the backend's LineIdentity
 * (splitByItems.ts): the line's own ordered_line_id/temp_item_id first — two
 * separately-added lines of the same menu item at the same price/tax (e.g.
 * "Fried Rice" added twice) must stay distinct, or paying one pools onto and
 * marks both as paid. Only when no line id exists does identity fall back to
 * menu item + source order + price + tax rate.
 */
export interface LineIdentity {
  lineId?: string;
  id: string;
  src: string;
  price?: number;
  tax?: number;
}

type LineIdentitySource = {
  _id?: unknown;
  menu_item_id?: unknown;
  order_id?: unknown;
  source_order_id?: unknown;
  ordered_line_id?: unknown;
  temp_item_id?: unknown;
  price?: unknown;
  consumption_tax_rate?: unknown;
};

const asNumber = (value: unknown): number | undefined =>
  typeof value === "number" ? value : undefined;

const identityOf = (item: LineIdentitySource): LineIdentity => ({
  lineId: getOrderedLineId(item) || undefined,
  id: String(item.menu_item_id ?? item._id ?? ""),
  src: String(item.source_order_id ?? item.order_id ?? ""),
  price: asNumber(item.price),
  tax: asNumber(item.consumption_tax_rate),
});

const identityKey = (identity: LineIdentity): string =>
  identity.lineId
    ? `line:${identity.lineId}`
    : [identity.id, identity.src, String(identity.price ?? ""), String(identity.tax ?? "")].join("|");

/** Distinct line identities across a set of order lines, de-duplicated by identityKey. */
export const distinctIdentities = (items: LineIdentitySource[]): LineIdentity[] => {
  const seen = new Map<string, LineIdentity>();
  items.forEach((item) => {
    const identity = identityOf(item);
    seen.set(identityKey(identity), identity);
  });
  return [...seen.values()];
};

/**
 * Does a stored paid row belong to the target line? A paid row with its own
 * ordered_line_id must match the target's line id exactly. A legacy row
 * without one (predating the discriminator) matches by menu item + source
 * order + price/tax only when that combination is unambiguous among
 * knownIdentities — otherwise it fails closed rather than pooling every
 * matching line together (mirrors splitByItems.ts's paidRowMatches).
 */
const paidRowMatches = (
  row: LineIdentitySource,
  target: LineIdentity,
  knownIdentities: LineIdentity[],
): boolean => {
  const rowLineId = getOrderedLineId(row) || undefined;
  if (target.lineId && rowLineId != null) {
    return rowLineId === target.lineId;
  }

  const rowId = String(row.menu_item_id ?? row._id ?? "");
  const rowSrc = String(row.source_order_id ?? row.order_id ?? "");
  if (rowId !== target.id || rowSrc !== target.src) {
    return false;
  }

  const rowPrice = asNumber(row.price);
  const sameIdSrc = knownIdentities.filter((i) => i.id === target.id && i.src === target.src);
  if (rowPrice == null) {
    return sameIdSrc.length === 1;
  }
  if (rowPrice !== target.price) {
    return false;
  }

  const rowTax = asNumber(row.consumption_tax_rate);
  if (rowTax == null) {
    return sameIdSrc.filter((i) => i.price === target.price).length === 1;
  }
  return rowTax === target.tax;
};

/**
 * Quantity already paid for one ordered line. `knownIdentities` should cover
 * every line in the order (via distinctIdentities) so legacy paid rows can be
 * resolved unambiguously; defaults to just the target line when the caller
 * has no broader context.
 */
export const getPaidQuantityForLine = (
  item: LineIdentitySource,
  paidItems: (LineIdentitySource & { quantity?: number })[] = [],
  knownIdentities?: LineIdentity[],
): number => {
  const target = identityOf(item);
  const identities = knownIdentities ?? [target];

  return paidItems.reduce(
    (total, row) => (paidRowMatches(row, target, identities) ? total + Number(row.quantity || 0) : total),
    0,
  );
};

export const buildSplitItemPayload = (item: Partial<SplitLikeItem>) => ({
  ...(getOrderedLineId(item) ? { ordered_line_id: getOrderedLineId(item) } : {}),
  menu_item_id: item._id || item.menu_item_id,
  order_id: item.order_id,
  quantity: Number(item.quantity || 0),
  ...(item.price != null ? { price: item.price } : {}),
  ...(item.consumption_tax_rate != null
    ? { consumption_tax_rate: item.consumption_tax_rate }
    : {}),
});
